import { useCallback, useEffect, useMemo, useState } from "react";

import { userAPI } from "../services/api";
import type { Handler } from "../types/handler";
import { useAppSelector } from "./useStore";

/**
 * The dispatchers a transport can be handed to, and which of them is you.
 *
 * ── Why this is a module-level cache and not a store slice ───────────────────
 *
 * Four screens draw handler bubbles and all four need the same dozen names, so
 * the list is fetched once per session and shared. It is not Redux because
 * nothing mutates it: a tenant's office staff changes when somebody is hired,
 * which is a page reload away, and a slice would be three files of ceremony
 * around a value that only ever arrives.
 *
 * The promise itself is cached, not just the result — four components mounting
 * in the same frame (the list's column, its scope control, a row's menu) would
 * otherwise fire four identical requests before any of them resolved.
 */

let cache: Handler[] | null = null;
let inflight: Promise<Handler[]> | null = null;
/** Every mounted hook, so one fetch settles all of them. */
const subscribers = new Set<(handlers: Handler[]) => void>();

const fetchHandlers = (): Promise<Handler[]> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = userAPI
    .getHandlers()
    .then((res) => {
      const handlers: Handler[] = res.data?.data?.handlers ?? [];
      cache = handlers;
      subscribers.forEach((notify) => notify(handlers));
      return handlers;
    })
    .catch(() => {
      /**
       * An empty roster rather than a thrown error.
       *
       * The bubbles that read this are an annotation on a list, never the list
       * itself. A picker that cannot open is a far smaller failure than an
       * orders page that refuses to render because a dozen names did not
       * arrive, and the next mount retries.
       */
      return [];
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/**
 * Drop the cached roster, so the next read fetches it again.
 *
 * For the places that change who is in it — inviting a colleague, deactivating
 * an account — which would otherwise leave every open tab's picker a person out
 * of date until a reload.
 */
export const invalidateHandlers = () => {
  cache = null;
};

export interface UseHandlers {
  /** Everyone a transport can be handed to, by first name. */
  handlers: Handler[];
  /** Resolve a bare id to a name — the board's cards carry ids alone. */
  byId: (id: string | null | undefined) => Handler | null;
  /** The signed-in user's id, which is what "Mine" resolves to. */
  meId: string | undefined;
  /** Has the roster landed? Keeps a picker shut rather than empty. */
  ready: boolean;
}

export function useHandlers(enabled = true): UseHandlers {
  const [handlers, setHandlers] = useState<Handler[]>(cache ?? []);
  const [ready, setReady] = useState(!!cache);

  const me = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    const notify = (next: Handler[]) => {
      if (active) setHandlers(next);
    };
    subscribers.add(notify);

    fetchHandlers().then((next) => {
      if (!active) return;
      setHandlers(next);
      setReady(true);
    });

    return () => {
      active = false;
      subscribers.delete(notify);
    };
  }, [enabled]);

  const index = useMemo(
    () => new Map(handlers.map((handler) => [handler._id, handler])),
    [handlers],
  );

  const byId = useCallback(
    (id: string | null | undefined) => (id ? (index.get(id) ?? null) : null),
    [index],
  );

  return { handlers, byId, meId: me?._id, ready };
}
